import {
  IconDefinition,
  faComments,
  faCube,
  faExclamationTriangle,
  faHome,
  faLayerGroup,
  faList,
  faShieldAlt,
  faShip,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "@trendmicro/react-sidenav/dist/react-sidenav.css";
import React, { useState } from "react";
import ClickOutside from "react-click-outsider";
import { useLocation, useNavigate } from "react-router-dom";
import { translate, ENVIRONMENT, useFeatureFlag } from "@shape/reef-shared";
import RequestFormModal from "components/RequestFormModal";
import { Routes, RoutesReplacer } from "routes";
import {
  NavIcon,
  NavItem,
  NavItemContainer,
  NavItemIcon,
  NavItemText,
  NavText,
  NavbarContent,
  SideNav,
  Spacer,
} from "./styled";

interface NavbarProps {
  isExpanded: boolean;
  onExpand: (isExpanded: boolean) => void;
}

const Navbar: React.FC<NavbarProps> = ({ isExpanded, onExpand }: NavbarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [requestFormModalIsOpen, setRequestFormModalIsOpen] = useState(false);
  const [, country, vesselId] = (window.location.pathname.match(
    /\/([A-z]*)\/facilities\/([A-z0-9]*)/
  ) ?? []) as Array<string>;
  const isNotMV32 = vesselId !== "MV32";
  const isBowtieViewEnabled = useFeatureFlag("bowtie-view", isNotMV32);

  const renderItem = (
    resourceKey: string,
    icon: IconDefinition,
    pathname?: string,
    onclick?: () => void
  ) => (
    <NavItem
      eventKey={pathname}
      active={location.pathname === pathname}
      onClick={() => {
        if (pathname) {
          navigate(pathname);
        } else if (onclick) {
          onclick();
        }
        onExpand(false);
      }}
    >
      <NavIcon>
        <NavItemContainer expanded={isExpanded}>
          <NavItemIcon>
            <FontAwesomeIcon icon={icon} />
          </NavItemIcon>
          {!isExpanded && <NavItemText>{translate(resourceKey)}</NavItemText>}
        </NavItemContainer>
      </NavIcon>
      <NavText>{translate(resourceKey)}</NavText>
    </NavItem>
  );

  if (location.pathname === Routes.LoginPage) return null;

  const shouldShowSces =
    vesselId !== "UARU" || (ENVIRONMENT !== "staging" && ENVIRONMENT !== "production");

  return (
    <ClickOutside
      onClickOutside={() => {
        onExpand(false);
      }}
    >
      <SideNav expanded={isExpanded} onToggle={() => onExpand(!isExpanded)}>
        <SideNav.Toggle />
        <NavbarContent expanded={isExpanded}>
          {renderItem("home", faHome, Routes.HomePage)}
          {country && vesselId ? (
            <>
              {renderItem("facilities", faShip, RoutesReplacer.FacilitiesPage({ country }))}
              {isBowtieViewEnabled &&
                renderItem(
                  "mahs",
                  faExclamationTriangle,
                  RoutesReplacer.MahsPage({ country, vesselId })
                )}
              {shouldShowSces &&
                renderItem("sces", faLayerGroup, RoutesReplacer.ScesPage({ country, vesselId }))}
              {renderItem("tags", faCube, RoutesReplacer.TagsPage({ country, vesselId }))}
              {renderItem("workOrders", faList, RoutesReplacer.WonosPage({ country, vesselId }))}
              {renderItem(
                "cto",
                faShieldAlt,
                RoutesReplacer.CaseToOperateDashboard({ country, vesselId })
              )}
            </>
          ) : (
            <></>
          )}
          <Spacer />
          {renderItem("supportRequestPage.title", faComments, undefined, () => {
            setRequestFormModalIsOpen(true);
          })}
        </NavbarContent>

        <RequestFormModal
          modalIsOpen={requestFormModalIsOpen}
          setModalIsOpen={setRequestFormModalIsOpen}
        />
      </SideNav>
    </ClickOutside>
  );
};

export default Navbar;
